import {
  type EntityBrowseEntity,
  entityBrowseMountedRowBucket,
  entityBrowsePageNumberBucket,
} from '@parcel/analytics';
import { capture } from '@parcel/analytics/browser';
import * as React from 'react';

/**
 * Fires once per browse scope when the first page of rows is available.
 * Measures end-to-end time from scope activation to first loaded rows.
 */
export function useEntityBrowseFirstRowsTiming(
  entity: EntityBrowseEntity,
  enabled: boolean,
  scopeKey: string,
  loadedCount: number,
  isLoading: boolean,
): void {
  const startedAtRef = React.useRef<number | null>(null);
  const capturedScopeRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!enabled) {
      startedAtRef.current = null;
      capturedScopeRef.current = null;
      return;
    }
    if (capturedScopeRef.current !== scopeKey) {
      startedAtRef.current = performance.now();
      capturedScopeRef.current = null;
    }
  }, [enabled, scopeKey]);

  React.useEffect(() => {
    if (!enabled || isLoading || loadedCount <= 0) return;
    if (capturedScopeRef.current === scopeKey) return;
    const startedAt = startedAtRef.current;
    if (startedAt === null) return;
    capturedScopeRef.current = scopeKey;
    capture('entity_browse_first_rows', {
      entity,
      latency_ms: Math.max(0, Math.round(performance.now() - startedAt)),
    });
  }, [enabled, entity, isLoading, loadedCount, scopeKey]);
}

/**
 * Captures later-page failure and retry without recording row IDs or cursors.
 * `loadedPageCount` is the number of successful pages already present.
 */
export function useEntityBrowseLaterPageSignals(
  entity: EntityBrowseEntity,
  enabled: boolean,
  isFetchNextPageError: boolean,
  loadedPageCount: number,
): {
  wrapFetchNextPage: (fetchNextPage: () => Promise<unknown>) => () => Promise<unknown>;
} {
  const failureCapturedRef = React.useRef(false);

  React.useEffect(() => {
    if (!enabled) {
      failureCapturedRef.current = false;
      return;
    }
    if (!isFetchNextPageError) {
      failureCapturedRef.current = false;
      return;
    }
    if (failureCapturedRef.current) return;
    failureCapturedRef.current = true;
    capture('entity_browse_later_page_failure', {
      entity,
      page_number_bucket: entityBrowsePageNumberBucket(loadedPageCount + 1),
    });
  }, [enabled, entity, isFetchNextPageError, loadedPageCount]);

  const wrapFetchNextPage = React.useCallback(
    (fetchNextPage: () => Promise<unknown>) => {
      return () => {
        if (enabled && isFetchNextPageError) {
          capture('entity_browse_later_page_retry', {
            entity,
            page_number_bucket: entityBrowsePageNumberBucket(loadedPageCount + 1),
          });
        }
        return fetchNextPage();
      };
    },
    [enabled, entity, isFetchNextPageError, loadedPageCount],
  );

  return { wrapFetchNextPage };
}

/** Reports the virtualized mounted-row window whenever it changes while browsing. */
export function reportEntityBrowseMountedRows(
  entity: EntityBrowseEntity,
  enabled: boolean,
  mountedCount: number,
): void {
  if (!enabled) return;
  capture('entity_browse_mounted_rows', {
    entity,
    mounted_row_count_bucket: entityBrowseMountedRowBucket(mountedCount),
  });
}
